// "Tell me a rule" -- design §6: entry -> preview -> save. Opens as a Dialog
// (>=720px, 560px max width) or a Sheet (<720px) per the responsive bands in
// §8. The LLM only proposes labels; the server resolves them against the
// caller's own vocabulary before anything is written (§6.2), and the user
// must explicitly Preview then Save -- Close/Escape never auto-commits or
// silently discards the in-session draft/sentence (§6 closing behavior).
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Label,
} from '@moneyskyline/ui';
import { viewportBandForWidth, type ShellViewportBand } from '@/app/navigation/viewportBand';
import {
  CommitConflictError,
  DraftExpiredError,
  RuleDraftUnknownOutcomeError,
  UnresolvedLabelError,
  useCommitRuleDraft,
  useCreateRuleDraft,
  useDiscardRuleDraft,
  useRuleDraft,
  type RuleDraft,
} from '@/api/hooks/useRuleDraft';
import { registerJob } from '../transactions/tools/jobRegistry';
import {
  emitRuleCommit,
  emitRuleDraft,
  newInteractionId,
  toErrorClass,
  viewportBandFromShellBand,
  type TelemetryContext,
} from '../transactions/telemetry';
import { RuleDraftPreview } from './RuleDraftPreview';

function useLocalViewportBand(): ShellViewportBand {
  const [band, setBand] = useState<ShellViewportBand>(() =>
    typeof window === 'undefined' ? 'desktop' : viewportBandForWidth(window.innerWidth),
  );
  useEffect(() => {
    const onResize = () => setBand(viewportBandForWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return band;
}

type BoxState =
  | { kind: 'entry' }
  | { kind: 'drafting' }
  | { kind: 'draft'; draft: RuleDraft }
  | { kind: 'unresolved'; phrase: string }
  | { kind: 'expired' }
  | { kind: 'draft-error' }
  | { kind: 'committed'; jobId: string; savedObject: 'context' | 'rule' }
  | { kind: 'commit-conflict' }
  | { kind: 'commit-failed'; draft: RuleDraft }
  | { kind: 'commit-unknown' }
  | { kind: 'commit-status-unavailable' }
  | { kind: 'discard-failed'; draft: RuleDraft };

/** §6/§7: a preview is dead once its TTL passes -- never re-served, only regenerated. */
function isDraftExpired(draft: RuleDraft): boolean {
  return new Date(draft.expires_at).getTime() <= Date.now();
}

export function TellMeARuleBox({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const band = useLocalViewportBand();
  const [sentence, setSentence] = useState('');
  const [state, setState] = useState<BoxState>({ kind: 'entry' });
  const [draftId, setDraftId] = useState<string | null>(null);
  const createDraft = useCreateRuleDraft();
  const commitDraft = useCommitRuleDraft();
  const discardDraft = useDiscardRuleDraft();
  const reread = useRuleDraft(draftId);

  function currentDraft(): RuleDraft | null {
    if (state.kind === 'draft') return state.draft;
    if (state.kind === 'discard-failed') return state.draft;
    if (state.kind === 'commit-failed') return state.draft;
    return null;
  }

  // §6/§7 "check expires_at on load/reopen/refresh": whenever this box opens
  // with a still-mounted draft, an expired preview is never left on screen
  // to be saved against -- it flips to the 'expired' state, whose only
  // action (Refresh preview) regenerates from the retained sentence.
  useEffect(() => {
    if (!open) return;
    const draft = currentDraft();
    if (draft && isDraftExpired(draft)) {
      // Synchronizing with the external wall-clock TTL on the open
      // transition, not derived render state -- same accepted pattern as
      // JobStatusRow's "last checked" stamp.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ kind: 'expired' });
    }
    // Only re-check on the open transition, not on every state change while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // §9: this box is opened from the tools menu -- 'tools' surface, matching
  // ReclassifyConfirmDialog's convention. One interaction_id per draft
  // attempt / per commit attempt, sharing across their attempt->settled
  // stages.
  const telemetryContext: TelemetryContext = {
    surface: 'tools',
    viewport_band: viewportBandFromShellBand(band),
    input_method: 'unknown',
  };
  function submit(value: string) {
    setState({ kind: 'drafting' });
    const interactionId = newInteractionId();
    emitRuleDraft(telemetryContext, interactionId, {
      stage: 'attempt',
      draft_id: null,
      draft_kind: null,
      preview_count: null,
      sample_count: null,
      error_class: null,
    });
    createDraft.mutate(
      { sentence: value },
      {
        onSuccess: (draft) => {
          setDraftId(draft.draft_id);
          setState({ kind: 'draft', draft });
          emitRuleDraft(telemetryContext, interactionId, {
            stage: 'ready',
            draft_id: draft.draft_id,
            draft_kind: draft.resolved.kind,
            preview_count: draft.preview_count,
            sample_count: draft.samples.length,
            error_class: null,
          });
        },
        onError: (error) => {
          if (error instanceof UnresolvedLabelError) {
            setState({ kind: 'unresolved', phrase: error.phrase });
            emitRuleDraft(telemetryContext, interactionId, {
              stage: 'unresolved',
              draft_id: null,
              draft_kind: null,
              preview_count: null,
              sample_count: null,
              error_class: toErrorClass(error),
            });
          } else {
            setState({ kind: 'draft-error' });
            emitRuleDraft(telemetryContext, interactionId, {
              stage: 'failed',
              draft_id: null,
              draft_kind: null,
              preview_count: null,
              sample_count: null,
              error_class: toErrorClass(error),
            });
          }
        },
      },
    );
  }

  function handlePreview() {
    const trimmed = sentence.trim();
    if (trimmed) submit(trimmed);
  }

  function handleCommit(hard: boolean) {
    const draft = currentDraft();
    if (!draft) return;
    const draftId = draft.draft_id;
    const interactionId = newInteractionId();
    emitRuleCommit(telemetryContext, interactionId, {
      stage: 'attempt',
      draft_id: draftId,
      hard,
      saved_object: null,
      job_id: null,
      rule_id: null,
      error_class: null,
    });
    commitDraft.mutate(
      { draftId, hard },
      {
        onSuccess: (result) => {
          const savedObject: 'context' | 'rule' = result.event_id && !result.rule_id ? 'context' : 'rule';
          registerJob(result.job_id, 'rule_commit');
          setState({ kind: 'committed', jobId: result.job_id, savedObject });
          setSentence('');
          setDraftId(null);
          emitRuleCommit(telemetryContext, interactionId, {
            stage: 'queued',
            draft_id: draftId,
            hard,
            saved_object: savedObject,
            job_id: result.job_id,
            rule_id: result.rule_id,
            error_class: null,
          });
        },
        onError: (error) => {
          let stage: 'expired' | 'conflict' | 'unconfirmed' | 'failed';
          if (error instanceof DraftExpiredError) {
            setState({ kind: 'expired' });
            stage = 'expired';
          } else if (error instanceof CommitConflictError) {
            setState({ kind: 'commit-conflict' });
            stage = 'conflict';
          } else if (error instanceof RuleDraftUnknownOutcomeError) {
            setState({ kind: 'commit-unknown' });
            stage = 'unconfirmed';
          } else {
            // §7: commit-failed preserves the preview -- Save/Discard stay
            // operable against the same retained draft, not a bare message.
            setState({ kind: 'commit-failed', draft });
            stage = 'failed';
          }
          emitRuleCommit(telemetryContext, interactionId, {
            stage,
            draft_id: draftId,
            hard,
            saved_object: null,
            job_id: null,
            rule_id: null,
            error_class: toErrorClass(error),
          });
        },
      },
    );
  }

  function handleDiscard() {
    const draft = currentDraft();
    if (!draft) return;
    discardDraft.mutate(draft.draft_id, {
      onSuccess: () => {
        setState({ kind: 'entry' });
        setDraftId(null);
      },
      onError: () => setState({ kind: 'discard-failed', draft }),
    });
  }

  function handleEditInstruction() {
    // Returns to entry, retains sentence text; the old draft is ineligible for Save until re-previewed (§6 item 1).
    setState({ kind: 'entry' });
    setDraftId(null);
  }

  function handleRerun() {
    const trimmed = sentence.trim();
    if (trimmed) submit(trimmed);
    else setState({ kind: 'entry' });
  }

  function handleRefreshPreview() {
    if (!draftId) {
      handleRerun();
      return;
    }
    reread.refetch().then((result) => {
      if (result.data && !isDraftExpired(result.data)) {
        setState({ kind: 'draft', draft: result.data });
      } else {
        // Either the GET failed, or it returned the same dead draft -- a GET
        // does not renew `expires_at`. Never re-serve it; regenerate from
        // the retained sentence instead (§6/§7).
        handleRerun();
      }
    });
  }

  function handleCheckStatus() {
    // `GET /api/rules/draft/{id}` never exposes a commit/save outcome on the
    // wire (RuleDraftData carries no status field) -- there is no honest way
    // to confirm from this response whether the earlier commit landed, so a
    // re-read always lands on "Save status unavailable" per §7, with repeat
    // commit disabled rather than guessing.
    reread.refetch().finally(() => setState({ kind: 'commit-status-unavailable' }));
  }

  // Closing/Cancel only hides the dialog -- the in-session draft and sentence
  // are preserved (§6: "Closing the surface preserves the in-session draft
  // and sentence; reopen with 'Continue preview'"). This component stays
  // mounted by its caller so local state survives the close.
  function close() {
    onOpenChange(false);
  }

  // Only a confirmed commit clears the session state -- there is nothing left to continue.
  function closeAfterCommit() {
    setState({ kind: 'entry' });
    setSentence('');
    setDraftId(null);
    onOpenChange(false);
  }

  const body = (
    <div data-testid="tell-me-a-rule-box">
      {state.kind === 'entry' && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handlePreview();
          }}
          className="flex flex-col gap-3"
        >
          <div>
            <Label htmlFor="rule-instruction">Your instruction</Label>
            <textarea
              id="rule-instruction"
              value={sentence}
              onChange={(event) => setSentence(event.target.value)}
              placeholder="Describe a merchant, account, or trip and how to categorize it."
              rows={3}
              className="mt-1 w-full min-h-16 rounded-md border border-input bg-background p-2 text-base sm:text-sm"
            />
          </div>
          {draftId && (
            <p className="text-xs text-muted-foreground" data-testid="rule-continue-hint">
              A previous preview is still available.{' '}
              <button type="button" className="underline" onClick={handleRefreshPreview}>
                Continue preview
              </button>
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={createDraft.isPending || !sentence.trim()}>
              Preview rule
            </Button>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {state.kind === 'drafting' && (
        <p role="status" data-testid="rule-drafting">
          Preparing preview…
        </p>
      )}

      {state.kind === 'unresolved' && (
        <div role="alert" data-testid="rule-unresolved-label">
          <p>
            Couldn&rsquo;t match &ldquo;{state.phrase}&rdquo;. Use an account, merchant, or type name you already have.
          </p>
          <Button type="button" variant="outline" className="mt-2" onClick={() => setState({ kind: 'entry' })}>
            Edit instruction
          </Button>
        </div>
      )}

      {state.kind === 'expired' && (
        <div data-testid="rule-draft-expired">
          <p>This preview expired. Preview it again before saving.</p>
          <Button type="button" className="mt-2" disabled={createDraft.isPending} onClick={handleRefreshPreview}>
            Refresh preview
          </Button>
        </div>
      )}

      {state.kind === 'draft-error' && (
        <div role="alert" data-testid="rule-draft-error">
          <p>Couldn&rsquo;t prepare a preview. Your transactions haven&rsquo;t changed.</p>
          <div className="mt-2 flex gap-2">
            <Button type="button" onClick={handleRerun} disabled={createDraft.isPending}>
              Try again
            </Button>
            <Button type="button" variant="outline" onClick={() => setState({ kind: 'entry' })}>
              Edit instruction
            </Button>
          </div>
        </div>
      )}

      {state.kind === 'commit-conflict' && (
        <div role="alert" data-testid="rule-commit-conflict">
          <p>Matching transactions changed since this preview. Review an updated preview.</p>
          <Button type="button" className="mt-2" onClick={handleRefreshPreview}>
            Refresh preview
          </Button>
        </div>
      )}

      {state.kind === 'commit-failed' && (
        <div role="alert" data-testid="rule-commit-failed">
          <p>Couldn&rsquo;t save this rule.</p>
          <RuleDraftPreview
            draft={state.draft}
            pending={commitDraft.isPending}
            discardPending={discardDraft.isPending}
            onCommit={handleCommit}
            onDiscard={handleDiscard}
            onEditInstruction={handleEditInstruction}
          />
        </div>
      )}

      {state.kind === 'commit-unknown' && (
        <div role="alert" data-testid="rule-commit-unknown">
          <p>We couldn&rsquo;t confirm whether it was saved. Refresh before trying again.</p>
          <Button type="button" className="mt-2" onClick={handleCheckStatus}>
            Check status
          </Button>
        </div>
      )}

      {state.kind === 'commit-status-unavailable' && (
        <div role="alert" data-testid="rule-commit-status-unavailable">
          <p>Save status unavailable.</p>
        </div>
      )}

      {state.kind === 'discard-failed' && (
        <div role="alert" data-testid="rule-discard-failed">
          <p>Couldn&rsquo;t discard this preview. Try again.</p>
          <RuleDraftPreview
            draft={state.draft}
            pending={commitDraft.isPending}
            discardPending={discardDraft.isPending}
            onCommit={handleCommit}
            onDiscard={handleDiscard}
            onEditInstruction={handleEditInstruction}
          />
        </div>
      )}

      {state.kind === 'draft' && (
        <RuleDraftPreview
          draft={state.draft}
          pending={commitDraft.isPending}
          discardPending={discardDraft.isPending}
          onCommit={handleCommit}
          onDiscard={handleDiscard}
          onEditInstruction={handleEditInstruction}
        />
      )}

      {state.kind === 'committed' && (
        <div role="status" data-testid="rule-draft-committed">
          <p>{state.savedObject === 'context' ? 'Trip context saved. Reviewing transactions…' : 'Rule saved. Reviewing transactions…'}</p>
          <div className="mt-2 flex gap-2">
            {state.jobId && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                View progress
              </Button>
            )}
            <Button type="button" onClick={closeAfterCommit}>
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (band === 'mobile') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Tell me a rule</SheetTitle>
            <SheetDescription>Describe how you want to categorize activity. Review it before saving.</SheetDescription>
          </SheetHeader>
          <div className="mt-3">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Tell me a rule</DialogTitle>
          <DialogDescription>Describe how you want to categorize activity. Review it before saving.</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

export default TellMeARuleBox;
